import React, { useCallback, useEffect, useState } from 'react';
import { Trash2, Loader2, FileCheck2 } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const SOURCE_LABEL = { self_report: 'Self report', manager_observation: 'Manager', operational: 'Operational' };
const SOURCE_TONE = {
  self_report: 'bg-sky-50 text-sky-700 border-sky-200',
  manager_observation: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  operational: 'bg-amber-50 text-amber-800 border-amber-200',
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export function EvidenceImportsTable({ selectedOrg, refreshKey, onChanged }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = selectedOrg ? `?organization_id=${encodeURIComponent(selectedOrg)}&limit=20` : '?limit=20';
      const res = await api.get(`/roe/actuals/evidence/data${q}`);
      setRows(res.data.records || []);
      setTotal(res.data.total || 0);
    } catch { setRows([]); setTotal(0); }
    finally { setLoading(false); }
  }, [selectedOrg]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const deleteBatch = async (batchId) => {
    if (!window.confirm('Remove every row from this import batch?')) return;
    setDeleting(batchId);
    try {
      const q = selectedOrg ? `?organization_id=${encodeURIComponent(selectedOrg)}` : '';
      const res = await api.delete(`/roe/actuals/evidence/batch/${batchId}${q}`);
      toast.success(`Removed ${res.data.deleted} evidence rows`);
      await load();
      onChanged?.();
    } catch (err) { toast.error(`Delete failed: ${err.response?.data?.detail || err.message}`); }
    finally { setDeleting(null); }
  };

  if (!loading && rows.length === 0) return null;

  return (
    <Card data-testid="evidence-imports-table">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <FileCheck2 className="h-4 w-4 text-rose-500" />
          Recent evidence imports
          <span className="text-xs font-normal text-slate-500" data-testid="evidence-imports-total">{total} row{total === 1 ? '' : 's'} total · showing latest {rows.length}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-500 border-b">
                  <th className="py-1.5 pr-3 font-medium">Cohort</th>
                  <th className="py-1.5 pr-3 font-medium">Program</th>
                  <th className="py-1.5 pr-3 font-medium">Period</th>
                  <th className="py-1.5 pr-3 font-medium">Source</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Score</th>
                  <th className="py-1.5 pr-3 font-medium text-right">n</th>
                  <th className="py-1.5 pr-3 font-medium">Imported</th>
                  <th className="py-1.5 font-medium">Batch</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 border-slate-100" data-testid={`evidence-row-${r.id}`}>
                    <td className="py-1.5 pr-3 font-medium text-slate-800">{r.cohort_id}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{r.program_name || '—'}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{r.period || '—'}</td>
                    <td className="py-1.5 pr-3">
                      <Badge variant="outline" className={`text-[10px] ${SOURCE_TONE[r.evidence_source] || ''}`}>{SOURCE_LABEL[r.evidence_source] || r.evidence_source}</Badge>
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums font-semibold text-slate-800">{r.application_score}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums text-slate-600">{r.sample_size}</td>
                    <td className="py-1.5 pr-3 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="py-1.5">
                      <button
                        type="button"
                        onClick={() => deleteBatch(r.batch_id)}
                        disabled={deleting === r.batch_id}
                        title={`Delete batch ${r.batch_id}`}
                        className="inline-flex items-center gap-1 font-mono text-[10px] text-slate-500 hover:text-rose-600 transition-colors disabled:opacity-50"
                        data-testid={`delete-batch-${r.batch_id}`}
                      >
                        {deleting === r.batch_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                        {r.batch_id?.replace('evb_', '')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[11px] text-slate-500">Deleting a batch removes every row imported in that upload. Chain metrics recalculate on the next refresh.</p>
        {deleting === null && rows.length > 0 && (
          <Button variant="ghost" size="sm" className="mt-1 h-6 px-2 text-[11px]" onClick={load} data-testid="evidence-imports-refresh">Refresh</Button>
        )}
      </CardContent>
    </Card>
  );
}

export default EvidenceImportsTable;
